import {
  checkCuratedSaleLive,
  checkMagnumSaleLive,
  claimNFT,
  getClaimedTokens,
  getMetadata,
  mintNFT,
  mintNFTMagnum,
} from 'logic/NFT';
import { COMMISSIONS } from './commissions';
import { TxLoaderState } from '@components/TxLoader';
import { isBefore } from 'date-fns';
import { ArtistCollectionFragment } from 'generated/graphql';
import { gql } from '@apollo/client';
import { CollectionImage } from './images';

export const ArtistCollection = gql`
  fragment artistCollection on Collection {
    slug
    id
    name
    projectName
    projectDescription
    tokenIdStart
    tokenIdEnd
    etherscan
    opensea
    cid
    type
    droptime
    season {
      seasonNumber
      commission {
        slug
        name
      }
    }
    image {
      url
    }
  }
`;

export type Collection = ArtistCollectionFragment;

export const getClaimedImages = async (c: Collection) => {
  if (c.slug === 'cristina-de-middel') {
    return await getClaimedTokens();
  }
  return [];
};

export const getCollectionMetadata = async (
  c: Collection,
  { id, index, tokenId }: { id: number; index: number; tokenId: number }
) => {
  /**
   * Different commissions store their metadata differently in Arweave
   */
  switch (c.season.commission.slug as COMMISSIONS) {
    case COMMISSIONS.foundry:
    case COMMISSIONS.community:
      return getMetadata(`${id}/${index + 1}`, c.cid);
    default:
      return getMetadata(tokenId, c.cid);
  }
};

export const getIsSaleLive = async (c?: Collection) => {
  if (!c) {
    return false;
  }
  // For curated commissions after first two drops, we check the contract directly to see if sale is live
  if (c.season.commission.slug === COMMISSIONS.curated && c.id > 2) {
    const active: boolean = await checkCuratedSaleLive(c.id);
    return active;
  }
  if (c.season.commission.slug === COMMISSIONS.magnum) {
    const active: boolean = await checkMagnumSaleLive(c.id);
    return active;
  }
  // If droptime has passed, gallery can be public
  return isBefore(new Date(c.droptime), new Date());
};

export const getMintByImageActionForCollection = (
  c: Collection,
  setTxState: (s: TxLoaderState) => unknown
) => {
  // Currently only the first collection uses claim by image
  if (c?.slug === 'cristina-de-middel') {
    return (img: CollectionImage) => claimNFT(img.tokenId, setTxState);
  }
  return null;
};

export const getMintActionForCollection = (c?: Collection) => {
  if (!c) {
    return null;
  }
  // The first collection mints by image vs. minting a random image
  if (c.slug === 'cristina-de-middel') {
    return null;
  }
  switch (c.season.commission.slug) {
    case COMMISSIONS.curated:
      return mintNFT;
    case COMMISSIONS.magnum:
      return mintNFTMagnum;
    case COMMISSIONS.foundry:
      null;
    case COMMISSIONS.community:
      null;
  }
};

export async function getImagesFromCollection(
  collection?: Collection
): Promise<CollectionImage[]> {
  // Unreleased collections do not have the token id ranges set yet
  if (!collection || !collection?.tokenIdStart || !collection?.tokenIdEnd) {
    return [];
  }

  const range = Array.from(
    { length: collection.tokenIdEnd - collection.tokenIdStart + 1 },
    (_, i) => collection.tokenIdStart + i
  );

  // For certain collections, we check contract to see if any images have been claimed
  const claimedTokenIds = await getClaimedImages(collection);

  const getCollectionImage = async (
    tokenId,
    index
  ): Promise<CollectionImage> => {
    const id = collection.id ?? 1000 * Math.random();
    let metadata = {
      name: '',
      image: '',
      attributes: null,
    };
    try {
      metadata = await getCollectionMetadata(collection, {
        id,
        index,
        tokenId,
      });
    } catch (e) {
      console.error(
        `Failed to get metadata for ${collection.name} ${tokenId}: ${e}`
      );
    }
    return {
      id: id,
      imageName: metadata?.name ?? null,
      imagePath: `/${collection.slug}/${tokenId}.jpg`,
      metadataImagePath: metadata.image,
      imageAttributes: metadata?.attributes ?? null,
      name: collection.name,
      projectName: collection.projectName,
      projectDescription: collection.projectDescription,
      cid: collection.cid,
      type: collection.type,
      tokenId,
      claimedStatus: claimedTokenIds.includes(tokenId)
        ? ('claimed' as const)
        : ('unclaimed' as const),
      opensea: collection.opensea,
      etherscan: collection.etherscan,
      slug: collection.slug,
    };
  };

  const promises = [];
  range.forEach((c, index) => {
    promises.push(getCollectionImage(c, index));
  });
  return await Promise.all(promises);
}
